import {
    Collapse,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popper,
    Typography,
} from '@mui/material'
import { Box, Stack } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAccount } from '../../context/account_context'
import { useConfigs } from '../../context/configs_context'
import { useThemeMode } from '../../context/theme_mode_context'
import Header from '../Header'
import { Navigation, navMenu } from './nav'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { SidebarMode } from '../Layout'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import {
    Home,
    Dashboard,
    People,
    PersonAdd,
    ManageAccounts,
    Security,
    Settings,
    Tune,
    Notifications,
    Lock,
    Description,
    Folder,
    FolderOpen,
    Archive,
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material'
import { LayoutConfigsInterface } from '../LayoutConfigs'

interface ContentProps {
    sidebarMode: number
    headerHeight: number
    setShowMobile(value: boolean): void
    setSidebarMode(value: number): void
    hideMinimizeSidebar?: boolean
}

interface MenuItensProps {
    itens: Navigation[]
    onlyIcons?: boolean
    handleCloseMenus?(closeMobile?: boolean): void
}

const MenuItens = ({
    itens,
    onlyIcons,
    handleCloseMenus: handleCloseMenusParent,
}: MenuItensProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()
    const [activeItem, setActiveItem] = useState<Navigation | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const handleEnter = (event: any, item: Navigation) => {
        if (item.subitems) {
            setAnchorEl(event.currentTarget)
            setActiveItem(item)
        }
    }

    const handleLeave = () => {
        timeoutRef.current = setTimeout(() => {
            handleCloseMenus()
        }, 50)
    }

    const open = Boolean(anchorEl) && !!activeItem

    const checkNav = (nav: Navigation) =>
        location.pathname === nav.link ||
        nav.subitems?.map((s) => s.link).includes(location.pathname)

    const handleNavigate = (nav: Navigation) => {
        if (nav.link) navigate(nav.link)
        else if (nav.subitems && nav.subitems.length) {
            const subitem = nav.subitems.find(
                (s) => accountInfo && s.validate(accountInfo, configs)
            )
            if (subitem?.link) navigate(subitem?.link)
        }

        handleCloseMenus(true)
    }

    const handleCloseMenus = (closeMobile?: boolean) => {
        setAnchorEl(null)
        setActiveItem(null)
        handleCloseMenusParent && handleCloseMenusParent(closeMobile)
    }

    return (
        <>
            <List
                sx={{
                    width: '100%',
                }}
                onMouseLeave={() => handleLeave()}
            >
                {itens
                    .filter(
                        (n) =>
                            accountInfo &&
                            n.validate(accountInfo, configs) &&
                            !n.hide
                    )
                    .map((nav, index) => (
                        <>
                            <Box position={'relative'}>
                                <ListItem
                                    key={index}
                                    disablePadding
                                    sx={{
                                        borderRadius: '10px',
                                        my: '2px',
                                        mx: '8px',
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(255, 255, 255, 0.12)',
                                        },
                                    }}
                                    onMouseEnter={(e) => handleEnter(e, nav)}
                                    onClick={() => {
                                        if (nav.link) handleNavigate(nav)
                                    }}
                                >
                                    <ListItemButton
                                        sx={{
                                            px: '12px',
                                            py: '8px',
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                width: '26px',
                                                height: '26px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                borderRadius: '8px',
                                                minWidth: '26px',
                                                color: 'secondary.main',
                                            }}
                                        >
                                            {nav.icon}
                                        </ListItemIcon>
                                        {!onlyIcons && (
                                            <ListItemText
                                                primary={t(nav.text)}
                                                primaryTypographyProps={{
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                }}
                                                sx={{
                                                    ml: '16px',
                                                    color: 'secondary.main',
                                                }}
                                            />
                                        )}
                                        {nav.subitems && !onlyIcons && (
                                            <ChevronRightIcon color="secondary" />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            </Box>
                        </>
                    ))}
            </List>
            {activeItem?.subitems && (
                <Popper
                    disablePortal
                    open={open}
                    anchorEl={anchorEl}
                    placement="right-start"
                    sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        mt: 1,
                        minWidth: 200,
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        boxShadow:
                            '0 8px 24px rgba(16, 24, 40, 0.18), 0 16px 32px rgba(16, 24, 40, 0.12)',
                    }}
                    onMouseEnter={() => {
                        if (timeoutRef.current) clearTimeout(timeoutRef.current)
                    }}
                    onMouseLeave={handleLeave}
                >
                    <MenuItens
                        itens={activeItem?.subitems}
                        handleCloseMenus={handleCloseMenus}
                    />
                </Popper>
            )}
        </>
    )
}

const DrawerContent = ({
    sidebarMode,
    headerHeight,
    setShowMobile,
    setSidebarMode,
    hideMinimizeSidebar,
}: ContentProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [activeItem, setActiveItem] = useState<Navigation>()
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()
    const [textColor, setTextColor] = useState('#dbeafe')

    useEffect(() => {
        if (configs.layoutConfigs) {
            const layoutConfigs = JSON.parse(
                configs.layoutConfigs
            ) as LayoutConfigsInterface
            if (layoutConfigs.sidebar?.textColor)
                setTextColor(layoutConfigs.sidebar.textColor)
        }
    }, [configs])

    const checkNav = (nav: Navigation) => location.pathname === nav.link /*  ||
        nav.subitems?.map((s) => s.link).includes(location.pathname) */

    const checkActiveItem = (nav: Navigation) =>
        !!activeItem && nav.text === activeItem?.text

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                fontWeight: 'normal',
                overflowY: 'scroll',
                '::-webkit-scrollbar': {
                    width: '0px',
                },
            }}
        >
            {sidebarMode === SidebarMode.Normal && !hideMinimizeSidebar && (
                <Stack
                    sx={{
                        minHeight: `${headerHeight}px`,
                        px: 1.75,
                        borderBottom: '1px solid ' + textColor,
                    }}
                    justifyContent={'center'}
                >
                    {/* <Typography color={'secondary'} fontWeight={'500'}>
                        {t('Navegación')}
                    </Typography> */}
                </Stack>
            )}

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    py: 1,
                    '::-webkit-scrollbar': {
                        width: '0px',
                    },
                }}
            >
                <List sx={{ px: 1.5 }}>
                    {navMenu
                        .filter(
                            (n) =>
                                accountInfo &&
                                n.validate(accountInfo, configs) &&
                                !n.hide
                        )
                        .map((nav) => (
                            <Box key={nav.text}>
                                <ListItemButton
                                    onClick={() => {
                                        if (nav.link) navigate(nav.link)
                                        else if (nav.subitems)
                                            setActiveItem(
                                                !checkActiveItem(nav)
                                                    ? nav
                                                    : undefined
                                            )
                                    }}
                                    sx={{
                                        borderRadius: 1.5,
                                        mb: 0.25,
                                        py: 0.75,
                                        px: 0.75,
                                        color: checkNav(nav)
                                            ? 'white'
                                            : textColor,
                                        backgroundColor: checkNav(nav)
                                            ? 'rgba(255, 255, 255, 0.2)'
                                            : 'transparent',
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(255, 255, 255, 0.1)',
                                        },
                                        ...(checkNav(nav) && {
                                            boxShadow:
                                                '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                                        }),
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            color: 'inherit',
                                            minWidth: 38,
                                            '& svg': { fontSize: '1.4rem' },
                                        }}
                                    >
                                        {nav.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t(nav.text)}
                                        primaryTypographyProps={{
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                        }}
                                    />
                                    {checkActiveItem(nav) ? (
                                        <ExpandLess
                                            sx={{
                                                color: textColor,
                                                fontSize: '1.4rem',
                                            }}
                                        />
                                    ) : (
                                        <ExpandMore
                                            sx={{
                                                color: textColor,
                                                fontSize: '1.4rem',
                                            }}
                                        />
                                    )}
                                </ListItemButton>

                                {nav.subitems && (
                                    <Collapse
                                        in={checkActiveItem(nav)}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        <List
                                            component="div"
                                            disablePadding
                                            sx={{ pl: 1.5 }}
                                        >
                                            {nav.subitems
                                                .filter(
                                                    (n) =>
                                                        accountInfo &&
                                                        n.validate(
                                                            accountInfo,
                                                            configs
                                                        ) &&
                                                        !n.hide
                                                )
                                                .map((subNav) => (
                                                    <ListItemButton
                                                        key={subNav.text}
                                                        onClick={() => {
                                                            if (subNav.link)
                                                                navigate(
                                                                    subNav.link
                                                                )
                                                        }}
                                                        sx={{
                                                            borderRadius: 1.5,
                                                            mb: 0.25,
                                                            py: 0.5,
                                                            px: 1,
                                                            color: checkNav(
                                                                subNav
                                                            )
                                                                ? 'white'
                                                                : textColor,
                                                            backgroundColor:
                                                                checkNav(subNav)
                                                                    ? 'rgba(255, 255, 255, 0.2)'
                                                                    : 'transparent',
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(255, 255, 255, 0.1)',
                                                            },
                                                            ...(checkNav(
                                                                subNav
                                                            ) && {
                                                                boxShadow:
                                                                    '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                                                            }),
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                color: 'inherit',
                                                                minWidth: 38,
                                                                '& svg': {
                                                                    fontSize:
                                                                        '1.4rem',
                                                                },
                                                            }}
                                                        >
                                                            {subNav.icon}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={t(
                                                                subNav.reducedText ??
                                                                    ''
                                                            )}
                                                            primaryTypographyProps={{
                                                                fontSize:
                                                                    '0.9rem',
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                        </List>
                                    </Collapse>
                                )}
                            </Box>
                        ))}
                </List>
            </Box>
        </Box>
    )
}

interface Props {
    drawerWidth: number
    headerHeight: number
    hideSideBar: number
    hideHeader: boolean
    sidebarMode: number
    setSidebarMode(value: number): void
}

const LeftPanel = ({
    drawerWidth,
    headerHeight,
    hideSideBar,
    hideHeader,
    sidebarMode,
    setSidebarMode,
}: Props) => {
    const { configs } = useConfigs()
    const { mode } = useThemeMode()
    const [showMobile, setShowMobile] = useState(false)
    const [bgColor, setBgColor] = useState(
        'linear-gradient(to bottom, #2563eb, #1e40af)'
    )

    useEffect(() => {
        let next =
            mode === 'dark'
                ? 'linear-gradient(to bottom, #1e293b, #0f172a)'
                : 'linear-gradient(to bottom, #2563eb, #1e40af)'
        if (configs.layoutConfigs) {
            const layoutConfigs = JSON.parse(
                configs.layoutConfigs
            ) as LayoutConfigsInterface
            if (layoutConfigs.sidebar?.bgColor)
                next = layoutConfigs.sidebar.bgColor
        }
        setBgColor(next)
    }, [configs, mode])

    return (
        <>
            {!hideHeader && (
                <Header
                    drawerWidth={drawerWidth}
                    setShowMobile={setShowMobile}
                    headerHeight={headerHeight}
                    hideSideBar={hideSideBar}
                    sidebarMode={sidebarMode}
                    setSidebarMode={setSidebarMode}
                />
            )}
            <Box className="leftPanel">
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={showMobile}
                    onClose={() => setShowMobile(false)}
                    PaperProps={{
                        sx: {
                            border: 'none',
                            width: drawerWidth,
                            display: {
                                xs: 'block',
                                lg: hideSideBar ? 'block' : 'none',
                            },
                            overflowY: 'visible',
                            background: bgColor,
                        },
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxShadow: '4px 0 10px rgba(0,0,0,0.15)',
                        },
                    }}
                >
                    <DrawerContent
                        sidebarMode={sidebarMode}
                        setShowMobile={setShowMobile}
                        headerHeight={headerHeight}
                        setSidebarMode={setSidebarMode}
                        hideMinimizeSidebar
                    />
                </Drawer>
                <Drawer
                    variant="permanent"
                    anchor="left"
                    PaperProps={{
                        sx: {
                            width: drawerWidth,
                            display: {
                                xs: 'none',
                                lg: hideSideBar ? 'none' : 'block',
                            },
                            height:
                                sidebarMode === SidebarMode.Normal
                                    ? '100vh'
                                    : `calc(100vh - ${headerHeight}px)`,
                            top:
                                sidebarMode === SidebarMode.Normal
                                    ? '0'
                                    : `${headerHeight}px`,
                            border: 'none',
                            overflowY: 'visible',
                            transition: 'width 0.2s linear',
                            background: bgColor,
                        },
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxShadow: '4px 0 10px rgba(0,0,0,0.15)',
                        },
                    }}
                    ModalProps={{
                        keepMounted: true,
                    }}
                >
                    <DrawerContent
                        sidebarMode={sidebarMode}
                        setShowMobile={setShowMobile}
                        headerHeight={headerHeight}
                        setSidebarMode={setSidebarMode}
                    />
                </Drawer>
            </Box>
        </>
    )
}

export default LeftPanel
